// Playback engine — port of the local app's Workspace.jsx engine (auto-pause latch, loop
// region, seek-resync suppression, currentMs throttle) re-homed on the PlayerAdapter
// contract. Framework-free: no DOM, no React, no timers of its own. Time arrives through
// adapter.onTime (~10 Hz polling for the IFrame — polling, unlike rAF, keeps running in a
// hidden tab, so loop and auto-pause stay correct while the tab is in the background).
//
// The invariants below are protected (docs/research/migration-assessment.md §5): every one of
// them re-fixes a bug that was found the hard way. Do not "simplify" them.

import type { PlayerAdapter } from '@nelnela/contracts';

export interface EngineLine {
  id: string;
  startMs: number;
  endMs: number;
}

export interface LoopRegion {
  startMs: number;
  endMs: number;
  label: string;
}

export type PlayerState = 'playing' | 'paused' | 'ended' | 'buffering';

export interface EngineState {
  currentMs: number;
  activeIdx: number;
  lastActiveIdx: number;
  playing: boolean;
  playerState: PlayerState;
  loop: LoopRegion | null;
  autoPause: boolean;
  rate: number;
  rates: number[];
}

/**
 * Optional adapter extension: the player reports rate changes made outside the engine (the
 * IFrame's own settings gear). Not in the contract's PlayerAdapter; probed by duck typing so
 * adapters without it (and test fakes) keep working.
 */
export interface RateReportingAdapter extends PlayerAdapter {
  onRateChange(cb: (rate: number) => void): () => void;
}

function isRateReporting(a: PlayerAdapter): a is RateReportingAdapter {
  return typeof (a as Partial<RateReportingAdapter>).onRateChange === 'function';
}

/** Lines are sorted by start; early-break once a line starts after ms. Gap → -1. */
export function findLineIdx(lines: readonly EngineLine[], ms: number): number {
  for (let i = 0; i < lines.length; i++) {
    if (ms >= lines[i].startMs && ms < lines[i].endMs) return i;
    if (lines[i].startMs > ms) break;
  }
  return -1;
}

// After any seek, boundary checks are suppressed for this long: the player needs a moment to
// resync and a stale prevIdx would otherwise fire an immediate auto-pause.
const SEEK_SUPPRESS_MS = 350;
// currentMs subscribers are notified only past this delta — the transcript re-renders per
// change, and 10 Hz × every line is not needed for a highlight that moves per line.
const CURRENT_MS_THROTTLE = 80;
// Auto-pause parks the playhead just before the line end so the last word is still shown.
const AUTO_PAUSE_BACKOFF_MS = 40;

function sameRates(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((r, i) => r === b[i]);
}

const defaultNow: () => number =
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? () => performance.now()
    : () => Date.now();

export class PlaybackEngine {
  private lines: readonly EngineLine[] = [];
  private state: EngineState;
  private readonly subs = new Set<() => void>();
  private readonly now: () => number;
  private readonly unsubs: Array<() => void> = [];

  // Boundary tracker: the line index seen at the previous tick (or re-derived after seeks and
  // structural line changes). Auto-pause fires when the playhead leaves prevIdx.
  private prevIdx = -1;
  // Latch: id of the line we already auto-paused at. Cleared once the playhead is inside a
  // different line — unless that line is still prevIdx (we are parked at its end).
  private pausedForKey: string | null = null;
  private suppressPauseUntil = 0;
  // A seek the engine itself issued must not be treated as a native seek in onSeek.
  private internalSeek = false;

  constructor(
    private readonly adapter: PlayerAdapter,
    opts: { now?: () => number } = {},
  ) {
    this.now = opts.now ?? defaultNow;
    const playing = adapter.isPlaying();
    this.state = {
      currentMs: adapter.currentTimeMs(),
      activeIdx: -1,
      lastActiveIdx: -1,
      playing,
      playerState: playing ? 'playing' : 'paused',
      loop: null,
      autoPause: false,
      rate: 1,
      rates: adapter.availableRates(),
    };
    this.unsubs.push(adapter.onTime(this.tick));
    this.unsubs.push(adapter.onStateChange(this.handleStateChange));
    this.unsubs.push(adapter.onSeek(this.handleSeek));
    if (isRateReporting(adapter)) {
      this.unsubs.push(adapter.onRateChange((rate) => this.update({ rate })));
    }
  }

  // ------------------------------------------------ store

  /** Same object reference until something changed (useSyncExternalStore-friendly). */
  getState(): EngineState {
    return this.state;
  }

  subscribe(cb: () => void): () => void {
    this.subs.add(cb);
    return () => {
      this.subs.delete(cb);
    };
  }

  private update(patch: Partial<EngineState>): void {
    // rates arrive as a fresh array each time; compare by content so an unchanged list is not
    // a change (subscribers re-render on every new snapshot).
    if (patch.rates && sameRates(patch.rates, this.state.rates)) patch.rates = this.state.rates;
    let changed = false;
    for (const k of Object.keys(patch) as Array<keyof EngineState>) {
      if (this.state[k] !== patch[k]) {
        changed = true;
        break;
      }
    }
    if (!changed) return;
    this.state = { ...this.state, ...patch };
    for (const cb of Array.from(this.subs)) cb();
  }

  private derived(
    currentMs: number,
  ): Pick<EngineState, 'currentMs' | 'activeIdx' | 'lastActiveIdx'> {
    const activeIdx = findLineIdx(this.lines, currentMs);
    return {
      currentMs,
      activeIdx,
      lastActiveIdx: activeIdx !== -1 ? activeIdx : this.state.lastActiveIdx,
    };
  }

  // ------------------------------------------------ lines

  // Structural edits shift line indices; reconcile the boundary tracker before the next tick
  // or auto-pause fires against a stale index.
  setLines(lines: readonly EngineLine[]): void {
    this.lines = lines;
    this.prevIdx = findLineIdx(lines, this.adapter.currentTimeMs());
    this.update(this.derived(this.state.currentMs));
  }

  // ------------------------------------------------ playback engine
  //
  // The boundary logic runs on every adapter tick. With the IFrame source that is ~10 Hz
  // getCurrentTime() polling — coarser than rAF but, unlike rAF (which browsers suspend in
  // hidden tabs), it keeps loop and auto-pause working while the tab is hidden. It reads only
  // internal fields, never the store snapshot, so ordering against React renders is moot.

  private tick = (ms: number): void => {
    const ls = this.lines;
    const idx = findLineIdx(ls, ms);
    const prev = this.prevIdx;

    // once inside a different line, the "already paused here" latch is stale
    if (idx !== -1 && this.pausedForKey != null && ls[idx].id !== this.pausedForKey) {
      const stillPrev = prev !== -1 && ls[prev] !== undefined && ls[prev].id === this.pausedForKey;
      if (!stillPrev) this.pausedForKey = null;
    }

    // Where the playhead is after this tick's actions. An internal seek's target counts as the
    // new position immediately (the <video> port read currentTime back after assigning it; the
    // IFrame's getCurrentTime() lags a seek, so we must not read it back here).
    let nowMs = ms;
    const lp = this.state.loop;
    if (lp && ms >= lp.endMs && this.state.playing) {
      this.internalSeek = true;
      this.adapter.seekTo(lp.startMs);
      nowMs = lp.startMs;
    } else if (
      this.state.autoPause &&
      this.state.playing &&
      prev !== -1 &&
      idx !== prev &&
      this.now() > this.suppressPauseUntil
    ) {
      const prevLine = ls[prev];
      if (prevLine && this.pausedForKey !== prevLine.id) {
        this.adapter.pause();
        this.internalSeek = true;
        nowMs = Math.max(prevLine.endMs - AUTO_PAUSE_BACKOFF_MS, prevLine.startMs);
        this.adapter.seekTo(nowMs);
        this.pausedForKey = prevLine.id;
      }
    }
    this.prevIdx = findLineIdx(ls, nowMs);
    if (Math.abs(this.state.currentMs - nowMs) > CURRENT_MS_THROTTLE) {
      this.update(this.derived(nowMs));
    }
  };

  private handleStateChange = (s: PlayerState): void => {
    // 'buffering' does not change intent: like <video>.paused, `playing` stays what it was
    // (a loop jump buffers and must still loop). 'ended' keeps the loop — the loop is a user
    // setting, not a playback fact.
    const playing = s === 'playing' ? true : s === 'buffering' ? this.state.playing : false;
    // availableRates() may only be known once the player is ready; refresh on every change.
    this.update({ playerState: s, playing, rates: this.adapter.availableRates() });
  };

  // A native seek (the IFrame's own scrubber) bypasses seekTo()'s bookkeeping — resync it here
  // so auto-pause doesn't yank the playhead back or skip its next pause.
  private handleSeek = (ms: number): void => {
    if (this.internalSeek) {
      this.internalSeek = false;
      return;
    }
    this.suppressPauseUntil = this.now() + SEEK_SUPPRESS_MS;
    this.pausedForKey = null;
    this.prevIdx = findLineIdx(this.lines, ms);
  };

  // ------------------------------------------------ commands

  seekTo(ms: number, opts: { play?: boolean } = {}): void {
    const target = Math.max(ms, 0);
    // Flag first: an adapter may report its own seek synchronously from inside seekTo().
    this.internalSeek = true;
    this.adapter.seekTo(target);
    this.suppressPauseUntil = this.now() + SEEK_SUPPRESS_MS;
    this.pausedForKey = null;
    this.prevIdx = findLineIdx(this.lines, target);
    this.update(this.derived(target)); // immediate — the throttle is for ticks only
    if (opts.play) this.adapter.play();
  }

  play(): void {
    this.adapter.play();
  }

  pause(): void {
    this.adapter.pause();
  }

  togglePlay(): void {
    if (this.state.playing) this.adapter.pause();
    else this.adapter.play();
  }

  private baseLineIdx(): number {
    if (this.state.activeIdx !== -1) return this.state.activeIdx;
    if (this.state.lastActiveIdx !== -1) return this.state.lastActiveIdx;
    return -1;
  }

  replayLine(): void {
    const i = this.baseLineIdx();
    if (i === -1) return;
    this.seekTo(this.lines[i].startMs, { play: true });
  }

  stepLine(dir: 1 | -1): void {
    const lines = this.lines;
    if (!lines.length) return;
    let i = this.baseLineIdx();
    if (i === -1) {
      // in a gap (or before the first line): next = first line ahead, prev = the one before it
      i = lines.findIndex((l) => l.startMs > this.state.currentMs);
      if (i === -1) i = lines.length - 1;
      if (dir < 0) i = Math.max(i - 1, 0);
    } else {
      i = Math.min(Math.max(i + dir, 0), lines.length - 1);
    }
    this.seekTo(lines[i].startMs, { play: true });
  }

  loopCurrentLine(): void {
    const i = this.baseLineIdx();
    if (i === -1) return;
    const l = this.lines[i];
    this.setLoop({ startMs: l.startMs, endMs: l.endMs, label: 'line' });
    this.seekTo(l.startMs, { play: true });
  }

  setLoop(loop: LoopRegion | null): void {
    this.update({ loop });
  }

  setAutoPause(on: boolean): void {
    this.update({ autoPause: on });
  }

  toggleAutoPause(): void {
    this.update({ autoPause: !this.state.autoPause });
  }

  /** No-op for rates the player does not offer (F4: speed = whatever availableRates says). */
  setRate(rate: number): void {
    const rates = this.adapter.availableRates();
    if (!rates.includes(rate)) {
      this.update({ rates });
      return;
    }
    this.adapter.setRate(rate);
    this.update({ rate, rates });
  }

  destroy(): void {
    for (const u of this.unsubs.splice(0)) u();
    this.subs.clear();
  }
}
